import { useEffect, useRef } from "react";
import type { KpiData,StatCardProps } from "./types"

export function getCardBorderClass(
  isFlashing: boolean,
  cardKey: string,
  value: number
) {
  if (
    !isFlashing &&
    cardKey === "flaggedCount" &&
    value === 0
  ) {
    return "border-green-400";
  }

  return "border-gray-200";
}


export function getValueColor(
  isStale: boolean,
  valueColor: string
) {
  return isStale
    ? "#9CA3AF"
    : valueColor;
}

export function renderPulse(
  pulse: boolean,
  isStale: boolean,
  effectiveDotColor: string
) {
  if (!pulse || isStale) {
    return null;
  }

  return (
    <span
      className="absolute h-4 w-4 rounded-full animate-ping"
      style={{
        backgroundColor: effectiveDotColor,
        opacity: 0.25,
      }}
    />
  );
}

export function scrollToSection(id: string) {
  document
    .getElementById(id)
    ?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
}

export function createSocketConnection(
  wsUrl: string,
  lastMsgAt: React.RefObject<number>,
  setIsStale: React.Dispatch<
    React.SetStateAction<boolean>
  >,
  setData: React.Dispatch<
    React.SetStateAction<KpiData>
  >,
  reconnect: () => void
) {
  const ws = new WebSocket(wsUrl);

  ws.onmessage = (evt) => {
    lastMsgAt.current = Date.now();

    setIsStale(false);

    try {
      const payload = JSON.parse(
        evt.data as string
      ) as Partial<KpiData>;

      setData((prev) => ({
        ...prev,
        ...payload,
      }));
    } catch {
      // ignore invalid payload
    }
  };

  ws.onerror = () => {
    ws.close();
  };

  ws.onclose = reconnect;

  return ws;
}
export function StatCard({
  label,
  scrollTo,
  dotColor,
  valueColor,
  pulse,
  value,
  subLabel,
  isStale,
  isFlashing,
  cardKey,
}: Readonly<StatCardProps>) {
  const valueRef =
    useRef<HTMLSpanElement>(null);

  const prevValue =
    useRef<number>(value);

  useEffect(() => {
    if (
      valueRef.current &&
      value !== prevValue.current
    ) {
      valueRef.current.textContent =
        String(value);

      prevValue.current = value;
    }
  }, [value]);

  const effectiveDotColor = isStale
    ? "#9CA3AF"
    : dotColor;

  return (
    <button
      type="button"
      onClick={() =>
        scrollToSection(scrollTo)
      }
      aria-label={`${label}: ${value}`}
      className={[
        "sc flex items-start gap-3 w-full px-4 py-3 rounded-xl",
        "bg-white border transition-all duration-200 text-left cursor-pointer",
        "hover:shadow-sm",
        isFlashing
          ? "kpi-border-flash"
          : "",
        getCardBorderClass(
          isFlashing,
          cardKey,
          value
        ),
      ].join(" ")}
    >
      <div className="relative mt-1 flex min-w-2.5 items-center justify-center">
        {renderPulse(
          pulse,
          isStale,
          effectiveDotColor
        )}

        <span
          className="relative h-2.5 w-2.5 rounded-full transition-colors duration-500"
          style={{
            backgroundColor:
              effectiveDotColor,
          }}
          aria-hidden="true"
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-0.5 text-[11px] font-medium text-gray-500">
          {label}
        </div>

        <div
          className="text-[26px] font-medium leading-none"
          style={{
            color: getValueColor(
              isStale,
              valueColor
            ),
          }}
        >
          <span ref={valueRef}>
            {value}
          </span>
        </div>

        <div className="mt-0.5 text-[11px] text-gray-400">
          {subLabel}
        </div>
      </div>
    </button>
  );
}
